import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { SERVICES, RECRUITMENT_AREAS } from "../data/site";
import PageHero from "../components/common/PageHero";
import SectionHeading from "../components/common/SectionHeading";
import Reveal from "../components/common/Reveal";
import CTABand from "../components/common/CTABand";
import useSeo from "../hooks/useSeo";

export default function Services() {
  useSeo({
    title: "Healthcare Recruitment Services",
    description:
      "Our active healthcare recruitment services: split fee recruitment, candidate sourcing, international healthcare recruitment support, recruitment partnerships and RPO support.",
    keywords:
      "Split Fee Recruitment, Candidate Sourcing, International Healthcare Recruitment, RPO Support, Healthcare Recruitment Services",
  });
  return (
    <>
      <PageHero
        overline="Talent Solutions"
        title="Recruitment services, healthcare-first"
        description="A focused set of active services — powered by recruitment intelligence and ready to scale across sectors."
      />
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={(i % 3) * 80}>
                  <Link to={`/services/${s.slug}`} className="group flex h-full flex-col rounded-2xl border border-border bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-md" data-testid={`services-card-${s.slug}`}>
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors group-hover:bg-gold group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-5 font-serif text-xl text-navy">{s.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{s.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
                      Learn more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading overline="Specialisms" title="Healthcare recruitment areas" description="We recruit across the core disciplines that keep healthcare services running." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {RECRUITMENT_AREAS.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={(i % 4) * 70}>
                  <div className="h-full rounded-2xl border border-border bg-background p-7">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h4 className="mt-5 font-serif text-lg text-navy">{a.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand
        title="Not sure which service fits?"
        description="Tell us what you need and we will recommend the right recruitment solution."
        primary={{ label: "Request Talent", to: "/employers#request" }}
        secondary={{ label: "Contact Us", to: "/contact" }}
      />
    </>
  );
}
